import React, { useState } from 'react';
import {
  Datagrid,
  TextField,
  FunctionField,
  BooleanInput,
  useListContext,
  useResourceContext,
  useRecordContext,
  SimpleShowLayout,
} from 'react-admin';
import LinkField from 'components/LinkField';
import { Button } from '@mui/material';
import { Check, Clear } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import MoneyField from 'components/MoneyField';
import PositionInput from 'components/PositionInput';
import SpecialtyInput from 'components/SpecialtyInput';
import PercentField from 'components/PercentField';
import { buildCustomExporter } from 'resources/rate_settings/buildCustomExporter';
import { DisplayHelpers, camelToSnakeCase } from 'utils/helpers';
import List from 'components/List';
import { Box } from '@mui/system';

import LocationTypeInput from 'components/LocationTypeInput';
import {
  getCreateLinkTo,
  getLinkToAndDisplay,
} from 'utils/rateSettings/PositionRateSettingsTabs.utils';
import BlendedRateField from 'components/BlendedRateField';
import { SettingsNestedOperatorSelect } from 'components/SettingsNestedOperatorSelect';
import {
  getMarginSource,
  getRateSettingsHash,
  getRateSettingsInfo,
} from './PositionRateSettingsTab.utils';
import { titleize } from 'inflection';
import useCheckAccess from 'hooks/useCheckAccess';
import useGate from 'hooks/useGate';
import { gateNames } from 'constants/statsig';

const Filters = () => {
  const resource = useResourceContext();
  const record = useRecordContext();
  const filters = [
    <PositionInput
      alwaysOn
      source="search.position_ids"
      multiple
      clearable
      filter={{ search: { position_ids: record.position_ids } }}
    />,
    <SpecialtyInput
      alwaysOn
      multiple
      clearable
      source="search.specialty_ids"
      positionSource="search.position_ids"
      optionText="label"
    />,
    <BooleanInput alwaysOn label="Valid For Jobs" source="for_job" />,
    <BooleanInput alwaysOn label="Valid For Assignments" source="for_assignment" />,
  ];

  if (resource === 'Market') {
    filters.push(
      <LocationTypeInput
        alwaysOn
        source="search.location_type_id"
        label="Apply Markup by Location Type"
      />,
    );
  }

  return filters;
};

const w2TravelSpecificFields = () => {
  const fields = [
    <MoneyField label="Margin W2 TCV Cents" source="settings.w2.travel.margin_w2_tcv_cents" />,

    <PercentField
      label="Margin W2 TCV Percent"
      source="settings.w2.travel.margin_percent_w2_tcv"
    />,
  ];

  return fields;
};

const getExporter = (filenamePrefix, isMarket, settingsNestedOperator) => (rateSettings) => {
  if (!rateSettings || rateSettings.length === 0) {
    return true;
  }

  return buildCustomExporter(rateSettings, filenamePrefix, isMarket, settingsNestedOperator);
};

const CreatePositionRateSettingButton = () => {
  const resource = useResourceContext();
  const record = useRecordContext();
  const checkAccess = useCheckAccess();
  const { display, linkTo } = getLinkToAndDisplay(resource, record);
  const action = display === 'Update' ? 'update' : 'create';
  const accessResource =
    resource === 'MarketLocationTypePositionRateSetting' ? 'MarketPositionRateSetting' : resource;

  if (!checkAccess(action, accessResource, 'settings')) {
    return null;
  }

  return (
    <FunctionField
      render={() => (
        <Button component={Link} size="small" color="primary" to={linkTo} state={{ record }}>
          {display}
        </Button>
      )}
    />
  );
};

type ChargeRateSettingsGridProps = {
  resource: string;
  settingsNestedOperator: string;
  setSettingsNestedOperator: (value: string) => void;
  blockCreateRateSettings: boolean;
  recordLocation: { primary_billing_group: object };
};

export const ChargeRateSettingsGrid = ({
  resource,
  settingsNestedOperator,
  setSettingsNestedOperator,
  blockCreateRateSettings,
  recordLocation,
}: ChargeRateSettingsGridProps) => {
  const { filterValues } = useListContext();
  const hasLocationTypeMarkup = Boolean(filterValues?.search?.location_type_id);

  const parentIdName = `${camelToSnakeCase(resource)}_id`;
  const isW2 = settingsNestedOperator.includes('w2');
  const ratesPrefix = isW2 ? 'W2' : '1099';
  const isState = resource === 'State';
  const isMarket = resource === 'Market';
  const isLocation = resource === 'Location';

  const markupSuffix = hasLocationTypeMarkup
    ? 'with_location_type_markup'
    : 'without_location_type_markup';

  const marketChargeRateLabel = (structure: '8hr' | '12hr') =>
    `${structure}${hasLocationTypeMarkup ? ' Location Markup' : ''} Charge Rate`;

  function getRateSettingsPrefix() {
    return settingsNestedOperator.replace('.', '_');
  }

  return (
    <>
      <SettingsNestedOperatorSelect
        setSettingsNestedOperator={setSettingsNestedOperator}
        settingsNestedOperator={settingsNestedOperator}
      />
      <Datagrid key={`${ratesPrefix}-${hasLocationTypeMarkup}`} bulkActionButtons={false}>
        {!blockCreateRateSettings && <CreatePositionRateSettingButton />}
        <FunctionField
          label="Active for Job"
          render={(v) => (v?.settings.can_post_job ? <Check /> : <Clear />)}
        />
        <FunctionField
          label="Active for Assignment"
          render={(v) => (v?.settings.can_post_assignment ? <Check /> : <Clear />)}
        />
        <LinkField source={parentIdName} />
        <TextField label="Position" source="position.display_name" />
        <TextField label="Specialty" source="specialty.label" />
        {isMarket && <TextField label="Market" source="market.display_name" />}
        {isLocation && (
          <FunctionField
            label="Active Rate Structure"
            render={({ settings }) => {
              return getRateSettingsInfo(settings, settingsNestedOperator, recordLocation)
                .activeStructureLabel;
            }}
          />
        )}
        {isMarket && (
          <MoneyField
            label={marketChargeRateLabel('8hr')}
            source={`non_blended_settings_charge_rate.${getRateSettingsPrefix()}_${markupSuffix}`}
            sortable={false}
          />
        )}
        {isMarket && (
          <MoneyField
            label={marketChargeRateLabel('12hr')}
            source={`blended_settings_charge_rate.${getRateSettingsPrefix()}_${markupSuffix}`}
            sortable={false}
          />
        )}
        {!isMarket && (
          <MoneyField
            label={`${!isLocation ? '8hr' : ''} Charge Rate`}
            source={`settings.${settingsNestedOperator}.charge_rate_cents`}
            sortable={false}
          />
        )}
        {isState && (
          <BlendedRateField label="12hr Charge Rate" classification={settingsNestedOperator} />
        )}

        <MoneyField
          label="Charge On Call Rate"
          source="settings.charge_on_call_hourly_rate_cents"
        />
        <MoneyField label="Agency Charge Rate" source="settings.agency_charge_rate_cents" />
        <FunctionField
          label={`${ratesPrefix} Charge Extended Rate`}
          render={({ settings }) => {
            const rateSettingsPrefix = getRateSettingsInfo(
              settings,
              settingsNestedOperator,
              recordLocation,
            ).rateSettingsPrefix;
            const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
            return DisplayHelpers.roundToTwoDecimals(
              rateSettingsHash?.[rateSettingsPrefix]?.charge_overtime_multiplier,
            );
          }}
        />
        <FunctionField
          label={`${ratesPrefix} Charge Second Extended Rate`}
          render={({ settings }) => {
            const rateSettingsPrefix = getRateSettingsInfo(
              settings,
              settingsNestedOperator,
              recordLocation,
            ).rateSettingsPrefix;
            const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
            return DisplayHelpers.roundToTwoDecimals(
              rateSettingsHash?.[rateSettingsPrefix]?.charge_double_overtime_multiplier,
            );
          }}
        />
        <FunctionField
          label={`${ratesPrefix} Charge Extended Rate Starts At`}
          render={({ settings }) => {
            const rateSettingsPrefix = getRateSettingsInfo(
              settings,
              settingsNestedOperator,
              recordLocation,
            ).rateSettingsPrefix;
            const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
            return DisplayHelpers.roundToTwoDecimals(
              rateSettingsHash?.[rateSettingsPrefix]?.charge_max_daily_regular_hours,
            );
          }}
        />
        <FunctionField
          label={`${ratesPrefix} Charge Second Extended Rate Starts At`}
          render={({ settings }) => {
            const rateSettingsPrefix = getRateSettingsInfo(
              settings,
              settingsNestedOperator,
              recordLocation,
            ).rateSettingsPrefix;
            const rateSettingsHash = getRateSettingsHash(settings, settingsNestedOperator);
            return DisplayHelpers.roundToTwoDecimals(
              (rateSettingsHash?.[rateSettingsPrefix]?.charge_max_daily_regular_hours || 0) +
                (rateSettingsHash?.[rateSettingsPrefix]?.charge_max_daily_overtime_hours || 0),
            );
          }}
        />
        <FunctionField
          label={`${ratesPrefix} Charge Weekly Extended Rate Starts At`}
          render={({ settings }) => {
            return DisplayHelpers.roundToTwoDecimals(
              getRateSettingsHash(settings, settingsNestedOperator)
                ?.charge_max_weekly_regular_hours,
            );
          }}
        />
        <FunctionField
          label={`${ratesPrefix} Charge Seventh Day Rule Enabled`}
          render={({ settings }) => {
            return getRateSettingsHash(settings, settingsNestedOperator)
              ?.charge_seventh_day_rule_enabled ? (
              <Check />
            ) : (
              <Clear />
            );
          }}
        />
        <PercentField label="On Call Fee Percent" source="settings.on_call_fee_percent" />

        {settingsNestedOperator === 'w2.travel' && w2TravelSpecificFields()}

        <PercentField
          label={`Total Margin Percent ${titleize(settingsNestedOperator.replace('.', ' '))}`}
          source={getMarginSource(settingsNestedOperator)}
        />

        <TextField label="Parent" source="id" />
      </Datagrid>
    </>
  );
};

const ChargePositionRateSettingsTab = () => {
  const record = useRecordContext();
  const resource = useResourceContext();
  const checkAccess = useCheckAccess();
  const { value: blockCreateRateSettings } = useGate(gateNames.blockCreateNewRateSettings);

  const [settingsNestedOperator, setSettingsNestedOperator] = useState(() => {
    if (resource !== 'Location') {
      return 'ten99';
    }

    return record.primary_billing_group?.plan === 'enterprise_w2' ? 'w2.local' : 'ten99';
  });
  const parentIdName = `${camelToSnakeCase(resource)}_id`;
  const isMarket = resource === 'Market';
  const resourceName = isMarket
    ? `${resource}LocationTypePositionRateSetting`
    : `${resource}PositionRateSetting`;
  const exporter = getExporter(
    `${record.__typename} ${record.id}`,
    isMarket,
    settingsNestedOperator,
  );
  const canCreateRateSettings =
    checkAccess('create', isMarket ? 'MarketPositionRateSetting' : resourceName, 'settings') &&
    !blockCreateRateSettings;
  const recordLocation = { primary_billing_group: record?.primary_billing_group ?? {} };

  return (
    <SimpleShowLayout>
      {canCreateRateSettings && (
        <Box display="flex">
          <Button
            component={Link}
            color="primary"
            to={getCreateLinkTo(resourceName, resource)}
            state={{ record: { [parentIdName]: record.id } }}
          >
            Add position rate settings
          </Button>
        </Box>
      )}
      <List
        resource={resourceName}
        filters={Filters()}
        hasCreate={false}
        filter={{
          [parentIdName]: record.id,
          include_parents: true,
          position_ids: record.position_ids,
          ...(resource === 'Market' && { location_type_id: record.location_type_id }),
        }}
        exporter={exporter}
        disableSyncWithLocation
      >
        <ChargeRateSettingsGrid
          resource={resource}
          settingsNestedOperator={settingsNestedOperator}
          setSettingsNestedOperator={setSettingsNestedOperator}
          blockCreateRateSettings={blockCreateRateSettings}
          recordLocation={recordLocation}
        />
      </List>
    </SimpleShowLayout>
  );
};

export default ChargePositionRateSettingsTab;
